import {FC, useCallback, useEffect, useState} from 'react'
import {useHistory} from 'react-router-dom'
import {listPromoBanners} from '../../promoBannersAdmin/promoBannerService'
import {PromoBanner} from '../../promoBannersAdmin/types'

// Route a banner click to the right place based on the admin-provided link:
//   - Same-origin absolute URL (https://<this-host>/foo)  -> SPA push('/foo')
//   - Relative path starting with '/'                      -> SPA push(path)
//   - Anything else (external http(s) URL, or a bare host) -> new tab
// Prevents the previous bug where an internal path like '/experience-search'
// was prefixed with 'https://' and opened in a new tab, dropping the session
// and forcing the user back to login.
const useBannerNavigate = () => {
  const history = useHistory()
  return useCallback(
    (url: string) => {
      if (!url) return
      const raw = url.trim()

      // Case 1: relative internal path.
      if (raw.startsWith('/')) {
        history.push(raw)
        return
      }

      // Case 2: absolute URL — same-origin goes through the router; anything
      // else opens externally. Anything that fails to parse falls through to
      // the external branch below (safer than throwing on admin input).
      if (/^https?:\/\//i.test(raw)) {
        try {
          const parsed = new URL(raw)
          if (parsed.origin === window.location.origin) {
            history.push(`${parsed.pathname}${parsed.search}${parsed.hash}`)
            return
          }
        } catch {
          // fall through to external open
        }
        window.open(raw, '_blank', 'noopener,noreferrer')
        return
      }

      // Case 3: bare host like 'example.com/foo' — treat as external.
      window.open(`https://${raw}`, '_blank', 'noopener,noreferrer')
    },
    [history]
  )
}

type BannerProps = {banner: PromoBanner; onClick: (url: string) => void}

const BorderlessBanner: FC<BannerProps> = ({banner, onClick}) => (
  <div
    onClick={() => onClick(banner.link)}
    role='button'
    aria-label={banner.alt_text || 'Promo banner'}
    style={{
      width: '100%',
      aspectRatio: '1514 / 653',
      background: `#151515 url(${banner.image}) center/cover no-repeat`,
      cursor: 'pointer',
      marginBottom: 0,
    }}
  />
)

// Armando 2026-09-10: se retiró el frame interno (border 1.5px rgba(21,21,21,0.35))
// que rodeaba la imagen. El tamaño del contenedor es suficiente y el recuadro no
// aporta al diseño. Se mantiene el fondo blanco para que los banners subidos como
// "bordered" en el admin no cambien de backdrop respecto a los "borderless".
const BorderedBanner: FC<BannerProps> = ({banner, onClick}) => (
  <div
    onClick={() => onClick(banner.link)}
    role='button'
    aria-label={banner.alt_text || 'Promo banner'}
    style={{
      width: '100%',
      aspectRatio: '1514 / 653',
      background: `#ffffff url(${banner.image}) center/cover no-repeat`,
      cursor: 'pointer',
    }}
  />
)

type PromoBannersProps = {variant?: 'all' | 'borderless' | 'bordered'}

export const PromoBanners: FC<PromoBannersProps> = ({variant = 'all'}) => {
  const navigate = useBannerNavigate()
  const [banners, setBanners] = useState<PromoBanner[]>([])
  const [loaded, setLoaded] = useState<boolean>(false)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const data = await listPromoBanners()
      if (!cancelled) {
        setBanners(data)
        setLoaded(true)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  if (!loaded || banners.length === 0) {
    return null
  }

  const borderless = banners.filter((b) => b.style === 'borderless')
  const bordered = banners.filter((b) => b.style === 'bordered')

  // Only the FIRST borderless keeps the black hero backdrop (visual continuity
  // with the Invitations section right above it). Any additional borderless
  // banners render over the default white background, matching the bordered
  // ones. Vertical gap between consecutive banners so they never touch.
  const [firstBorderless, ...restBorderless] = borderless
  const BANNER_GAP = 40

  return (
    <>
      {variant !== 'bordered' && firstBorderless && (
        // Bottom padding = BANNER_GAP so the first banner keeps a 40px black
        // breathing strip before the white section (or footer) below it.
        <div
          style={{
            background: '#151515',
            padding: variant === 'borderless' ? `0 0 ${BANNER_GAP}px` : `0 60px ${BANNER_GAP}px`,
          }}
        >
          <BorderlessBanner banner={firstBorderless} onClick={navigate} />
        </div>
      )}
      {variant !== 'bordered' && restBorderless.length > 0 && (
        // Full-width white strip that breaks out of the black Invitations wrapper
        // (same negative-margin trick used by .promo-banners-bordered-fullwidth
        // in _nb-experience-preview.scss) so borderless banners #2..N sit over
        // white instead of inheriting the black backdrop of the parent.
        <div
          className='promo-banners-white-fullwidth'
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: BANNER_GAP,
            background: '#ffffff',
            padding: `${BANNER_GAP}px 104px`,
            width: 'calc(100% + 208px)',
            marginLeft: -104,
            marginRight: -104,
          }}
        >
          {restBorderless.map((b) => (
            <BorderlessBanner key={b.id} banner={b} onClick={navigate} />
          ))}
        </div>
      )}
      {variant !== 'borderless' && bordered.length > 0 && (
        <div
          className='promo-banners-bordered-fullwidth'
          style={{display: 'flex', flexDirection: 'column', gap: BANNER_GAP, paddingTop: BANNER_GAP}}
        >
          {bordered.map((b) => (
            <BorderedBanner key={b.id} banner={b} onClick={navigate} />
          ))}
        </div>
      )}
    </>
  )
}
